import { AxiosResponse } from 'axios'
import { BaseResponseMapper } from './BaseMapper'
import {
  DropBox,
  DropBoxes,
  DropBoxMonitoringSummary,
} from '@/domain/entities/DropBox'
import { Pagination } from '@/domain/entities/Pagination'

interface DropBoxApiItem {
  id?: number
  external_id?: string
  name?: string
  serial_number?: string
  lat?: number
  long?: number
  address?: string
  phone_number?: string | number
  agent_code?: string
  status?: string
  visibility?: string
  created_at?: string
  updated_at?: string
  machine_status?: string
  machine_condition?: string
  printer_status?: string
  scanner_status?: string
  door_package_input_status?: string
  door_package_output_status?: string
  capacity_percentage?: number
}

export class DropBoxMapper extends BaseResponseMapper {
  public convertDropBoxesFromApi(result: AxiosResponse): DropBoxes {
    const { data } = result
    // Support both nested pagination and root-level pagination fields
    let paginationData: Pagination
    if (data.pagination) {
      // Nested pagination structure (most common)
      paginationData = new Pagination(
        data.pagination.page,
        data.pagination.limit || data.pagination.per_page,
        data.pagination.total_count ||
          data.pagination.total_item ||
          data.pagination.total_data
      )
    } else {
      // Root-level pagination fields (as per ticket spec)
      paginationData = new Pagination(
        data.page,
        data.limit || data.per_page,
        data.total_count || data.total_item || data.total_data
      )
    }
    const dataArray = (
      Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []
    ) as DropBoxApiItem[]

    const summary = data.summary
      ? ({
          online: data.summary.online ?? 0,
          onlineWithIssues: data.summary.online_with_issues ?? 0,
          offline: data.summary.offline ?? 0,
        } as DropBoxMonitoringSummary)
      : undefined

    return new DropBoxes(
      paginationData,
      dataArray.map((item: DropBoxApiItem) => {
        return new DropBox(
          item.id,
          item.external_id,
          item.name,
          item.serial_number,
          item.lat,
          item.long,
          item.address,
          item.phone_number != null ? String(item.phone_number) : undefined,
          item.agent_code,
          item.status,
          item.visibility,
          item.created_at,
          item.updated_at,
          item.machine_status,
          item.machine_condition,
          item.printer_status,
          item.door_package_input_status,
          item.door_package_output_status,
          item.scanner_status,
          undefined,
          item.capacity_percentage
        )
      }),
      summary
    )
  }

  public convertDropBoxDetailFromApi(result: AxiosResponse): DropBox | null {
    const { data } = result
    if (!data) return null

    // Support both { data: [item] }, { data: item }, and [item]
    let item: DropBoxApiItem | null = null
    if (data.data) {
      item = Array.isArray(data.data) ? data.data[0] : data.data
    } else if (Array.isArray(data)) {
      item = data[0]
    } else {
      // Maybe the data IS the item itself
      item = data
    }

    if (!item || (!item.id && !item.external_id)) {
      return null
    }

    return new DropBox(
      item.id,
      item.external_id,
      item.name,
      item.serial_number,
      item.lat,
      item.long,
      item.address,
      item.phone_number != null ? String(item.phone_number) : undefined,
      item.agent_code,
      item.status,
      item.visibility,
      item.created_at,
      item.updated_at,
      item.machine_status,
      item.machine_condition,
      item.printer_status,
      item.door_package_input_status,
      item.door_package_output_status,
      item.scanner_status,
      undefined,
      item.capacity_percentage
    )
  }
}
